import { useDeepCompareEffect } from "@react-hookz/web";
import { useCallback, useEffect, useState } from "react";
import { Waiting } from "../../supabase/hooks/use-supabase-loader";

import type { UseSupabaseLoaderState } from "../../supabase/hooks/types";
import type { DependencyList } from "react";

export type UseApiLoaderResponse<T> = UseSupabaseLoaderState<T> & { reload: () => void };
/**
 * An Effect for loading data from API with a reload handler
 * NOTE: Reloading whilst loading dose not cancel and can show the first requests data
 */
export function useApiLoader<T>(
  callback: () => Promise<T>,
  deps: DependencyList,
): UseApiLoaderResponse<T> {
  const [reloadCount, setReloadCount] = useState(0);
  const [state, setState] = useState<UseSupabaseLoaderState<T>>({
    data: null,
    loading: true,
    error: null,
  });
  useEffect(() => {
    if (!state.loading) return;
    if (reloadCount === 0) return;
    callback()
      .then((data) => setState({ data, loading: false, error: null }))
      .catch((error) => {
        if (!(error instanceof Waiting)) {
          // oxlint-disable-next-line typescript/no-unsafe-type-assertion
          setState({ data: null, loading: false, error: error as Error });
        }
      });
  }, [state.loading, reloadCount]); // Do not add callback

  const reload = useCallback(() => {
    setState({ data: null, loading: true, error: null });
    // Reload even if you were still loading
    setReloadCount(reloadCount + 1);
  }, [reloadCount]);

  useDeepCompareEffect(reload, deps);

  return { ...state, reload };
}
